import * as React from "react";
import { Row, Col } from "react-bootstrap";
import { PlusCircle, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import MiniBarChart from "./MiniBarChart";
import { IOrgBreakdown, IOrgDetailData } from "../analytics.types";

interface IOrgDetailPanelProps {
  org: IOrgBreakdown;
  detail: IOrgDetailData;
  dateLabel: string;
}

const OrgDetailPanel = ({ org, detail, dateLabel }: IOrgDetailPanelProps) => {
  const showDeviceGrowth = org.devicesAdded > 0;
  const showAlertsData = org.alertsSent > 0;

  const visibleCardsCount =
    (showDeviceGrowth ? 1 : 0) + (showAlertsData ? 2 : 0);

  const columnSize =
    visibleCardsCount === 1 ? 12 : visibleCardsCount === 2 ? 6 : 4;

  const deviceGrowthMax = Math.max(
    ...detail.deviceGrowth.map((point) => point.count),
    1,
  );

  const severityRows = [
  { severity: 'High', color: 'var(--fuji-severity-high)' },
  { severity: 'Medium', color: 'var(--fuji-severity-medium)' },
  { severity: 'Low', color: 'var(--fuji-severity-low)' },
  ].map((severityItem) => {
    const matchingSeverity = detail.alertsBySeverity.find(
      (alertItem) =>
        alertItem.severity.toLowerCase() ===
        severityItem.severity.toLowerCase(),
    );

    return {
      severity: severityItem.severity,
      color: matchingSeverity?.color || severityItem.color,
      count: matchingSeverity?.count || 0,
    };
  });

  const totalSeverityAlerts = severityRows.reduce(
    (sum, alertItem) => sum + alertItem.count,
    0,
  );

  return (
    <div className="org-detail-panel">
      <div className="detail-header">
        <h3>{org.orgName}</h3>
        <span className="detail-period">{dateLabel}</span>
      </div>

      <Row className="detail-body">
        {showDeviceGrowth && (
          <Col md={12} lg={columnSize}>
            <div className="detail-card">
              <div className="detail-card-title">
                <PlusCircle size={16} strokeWidth={1.5} />
                <span>Device Growth</span>
              </div>

              <MiniBarChart
                 data={detail.deviceGrowth.map((point) => ({
                    label: point.label,
                    value: point.count,
                  }))}
                maxVal={deviceGrowthMax}
                color="var(--fuji-secondary-color)"
              />

              <div className="detail-card-footer">
                <span>+{org.devicesAdded} devices added</span>
              </div>
            </div>
          </Col>
        )}

        {showAlertsData && (
  <Col md={12} lg={columnSize}>
    <div className="detail-card">
      <div className="detail-card-title">
        <AlertTriangle size={16} strokeWidth={1.5} />
        <span>By Severity</span>
      </div>

      <div className="detail-severity-list">
        {severityRows.map((alert) => (
          <div key={alert.severity} className="detail-severity-row">
            <span className="sev-dot" style={{ backgroundColor: alert.color }} />
            <span className="sev-label">{alert.severity}</span>
            <div className="sev-bar-bg">
              <div
                className="sev-bar-fill"
                style={{
                  width: `${totalSeverityAlerts > 0 ? (alert.count / totalSeverityAlerts) * 100 : 0}%`,
                  backgroundColor: alert.color,
                }}
              />
            </div>
            <span className="sev-count">{alert.count}</span>
          </div>
        ))}
      </div>
    </div>
  </Col>
)}

        {showAlertsData && (
          <Col md={12} lg={columnSize}>
            <DeliveryCard detail={detail} />
          </Col>
        )}
      </Row>
    </div>
  );
};

const DeliveryCard = ({ detail }: { detail: IOrgDetailData }) => {
  const { success, failed, total } = detail.alertSuccessFailure;
  const successPct = total > 0 ? Math.round((success / total) * 100) : 0;
  const failedPct = total > 0 ? Math.round((failed / total) * 100) : 0;

  return (
    <div className="detail-card">
      <div className="detail-card-title">
        <CheckCircle2 size={16} strokeWidth={1.5} />
        <span>Delivery</span>
      </div>

      <div className="delivery-stats">
        <div className="delivery-row success">
          <CheckCircle2 size={14} />
          <span className="delivery-label">Success</span>
          <span className="delivery-value">{success}</span>
          <span className="delivery-pct">({successPct}%)</span>
        </div>

        <div className="delivery-row failed">
          <XCircle size={14} />
          <span className="delivery-label">Failed</span>
          <span className="delivery-value">{failed}</span>
          <span className="delivery-pct">({failedPct}%)</span>
        </div>

        <div className="delivery-bar">
          <div
            className="delivery-bar-success"
            style={{ width: `${successPct}%` }}
          />
          <div
            className="delivery-bar-failed"
            style={{ width: `${failedPct}%` }}
          />
        </div>
      </div>

      <div className="detail-sources">
        <span className="detail-sources-label">By Source</span>
        <div className="detail-sources-list">
          {detail.alertsBySource
            .filter((source) => source.count > 0)
            .map((source) => (
              <div key={source.source} className="detail-source-chip">
                <span className="chip-name">{source.source}</span>
                <span className="chip-count">{source.count}</span>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

export default OrgDetailPanel;
